import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useFiles } from "@/context/FilesContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function CompanySwitcher() {
  const { companies, current, company, switchCompany } = useCompany();
  const { refreshKey } = useFiles();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState({});

  useEffect(() => {
    api.get("/approvals/count").then((r) => setPending(r.data.by_company || {})).catch(() => {});
  }, [refreshKey, location.pathname, current]);

  const pick = (id) => {
    setOpen(false);
    if (id === current) return;
    switchCompany(id);
    if (/^\/(fichiers|fournisseurs)\/.+/.test(location.pathname)) navigate(location.pathname.split("/").slice(0, 2).join("/"));
  };
  const others = Object.entries(pending).filter(([id]) => id !== current).reduce((s, [, n]) => s + n, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative flex items-center gap-2 h-10 pl-2 pr-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 max-w-[9rem] sm:max-w-[15rem] text-left"
          data-testid="company-switcher">
          <span className="h-6 w-6 rounded flex items-center justify-center text-white shrink-0" style={{ background: company?.color || "#334155" }}>
            <Building2 className="h-3.5 w-3.5" />
          </span>
          <span className="block min-w-0">
            <span className="hidden sm:block text-[10px] uppercase tracking-wider text-slate-500 leading-none">Société</span>
            <span className="block text-sm font-semibold truncate leading-tight" data-testid="company-current-name">{company?.name || "…"}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
          {others > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-amber-600 text-[10px] text-white flex items-center justify-center"
            title="Validations en attente dans d'autres sociétés" data-testid="company-switcher-pending">{others}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1" data-testid="company-switcher-menu">
        <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Changer de société</div>
        {companies.map((c) => (
          <button key={c.id} onClick={() => pick(c.id)} data-testid={`company-option-${c.id}`}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left hover:bg-slate-100 ${c.id === current ? "bg-slate-50 font-semibold" : ""}`}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
            <span className="flex-1 truncate">{c.name}</span>
            {pending[c.id] > 0 && <span className="text-[10px] font-bold bg-amber-600 text-white rounded-full px-1.5">{pending[c.id]}</span>}
            {c.id === current && <Check className="h-4 w-4 text-[#1E3A8A]" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
